import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { RiErrorWarningLine } from 'react-icons/ri';
import { getToday, ajustedDate } from '../../../../utils/DateUtils';
import OpenCashInitialView from './openCashInitialView';
import OpenedCashView from './openedCashView';
import ClosedCashView from './closedCashView';
import FinancialService from '../../../../rest/financialService';
import OrderService from '../../../../rest/orderService';
import ModalInfo from '../../../../components/lib/modalInfo';
import { normalizePaymentMethod, stripPrefix } from '../../../../utils/StringUtils';
import RegistryTransactionModal from './registryTransactionModal';
import { toast } from "react-toastify";
import { useCashFlowUpdates } from '../../../../ws/useCashFlowUpdates';


const getClientCode = () => {
    const code = sessionStorage.getItem('clientCode');
    return code && code.trim() !== '' ? code : null;
};

const getUserName = () => {
    const name = sessionStorage.getItem('name');
    return name && name.trim() !== '' ? name : '';
};

const INITIAL_CASH_FLOW = {
    registry_status: '',
    opening_time: '',
    closing_time: '',
    start_value: 0,
    inputs_partial_value: 0,
    outputs_partial_value: 0,
    total_credit_charges: 0,
    final_value: 0,
    opened_by: '',
    closed_by: '',
    closing_report: '',
    closing_status: '',
    client_code: null
};

const Registry = ({ onTransactionUpdate, transaction }) => {
    const isSubmittingRef = useRef(false);

    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [isOpened, setIsOpened] = useState(false);
    const [isClosed, setIsClosed] = useState(false);
    const [dailyCashFlow, setDailyCashFlow] = useState(INITIAL_CASH_FLOW);
    const [inputValue, setInputValue] = useState(0.00);
    const [outputValue, setOutputValue] = useState(0.00);
    const [creditCharges, setCreditCharges] = useState(0);
    const [orders, setOrders] = useState([]);
    const [canceledSummary, setCanceledSummary] = useState({ count: 0, total: 0 });
    const [partialData, setPartialData] = useState(null);
    const [disabledButtons, setDisabledButtons] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [fieldToEdit, setFieldToEdit] = useState(null);

    const searchInput = getToday();
    const todayLabel = new Date().toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });

    const resetCashFlow = (clientCode) => ({
        ...INITIAL_CASH_FLOW,
        client_code: clientCode
    });

    useEffect(() => {
        const clientCode = getClientCode();
        if (!clientCode) {
            toast.error("Sessão inválida. Faça login novamente.");
            return;
        }
        setDailyCashFlow(resetCashFlow(clientCode));
    }, []);

    useEffect(() => {
        if (transaction && onTransactionUpdate) {
            onTransactionUpdate(transaction);
        }
    }, [transaction]);

    const loadCashState = useCallback(async () => {
        try {
            const data = await FinancialService.getIfOpen(searchInput);
            if (data) {
                setIsOpened(true);
                setDailyCashFlow(prev => ({
                    ...data,
                    client_code: prev.client_code
                }));
                setInputValue(data.inputs_partial_value || 0);
                setOutputValue(data.outputs_partial_value || 0);
            } else {
                setIsOpened(false);
            }

            const charges = await FinancialService.getCreditCharges(searchInput);
            if (charges !== null) setCreditCharges(charges);
        } catch (error) {
            console.error(error);
        }
    }, [searchInput]);

    useEffect(() => {
        loadCashState();
    }, [loadCashState]);

    useCashFlowUpdates({
        clientCode: getClientCode(),
        onOpened: loadCashState,
        onClosed: loadCashState,
    });

    const retrievePartialCashFlowData = async () => {
        if (!isOpened || !dailyCashFlow.opening_time) return;

        try {
            const data = await FinancialService.retrievePartialCashFlow(dailyCashFlow.opening_time);
            if (data) {
                setPartialData(data);
                setDailyCashFlow(prev => ({
                    ...prev,
                    final_value:
                        (prev.start_value || 0) +
                        (prev.inputs_partial_value || 0) -
                        (prev.outputs_partial_value || 0)
                }));
            }
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        retrievePartialCashFlowData();
    }, [dailyCashFlow.opening_time, isOpened]);

    useEffect(() => {
        if (dailyCashFlow.opening_time && !isClosed) {
            const loadOrders = async () => {
                const range = [
                    dailyCashFlow.opening_time,
                    dailyCashFlow.closing_time || ajustedDate()
                ];
                try {
                    const data = await OrderService.getAllOrders(0, 9999, range);
                    setOrders(data?.content || []);
                } catch (error) {
                    console.error(error);
                }
            };
            loadOrders();
        }
    }, [dailyCashFlow.opening_time, isClosed]);

    useEffect(() => {
        const summary = orders.reduce((acc, order) => {
            if (order.status === 'CANCELED') {
                acc.count += 1;
                acc.total += order.total;
            }
            return acc;
        }, { count: 0, total: 0 });

        setCanceledSummary(summary);
    }, [orders]);

    useEffect(() => {
        if (partialData?.stats && Object.keys(disabledButtons).length === 0) {
            const initialButtons = Object.keys(partialData.stats).reduce((acc, tipo) => {
                acc[tipo] = false;
                return acc;
            }, {});
            setDisabledButtons(initialButtons);
        }
    }, [partialData]);

    const processEntries = (prefix) =>
        partialData?.stats
            ? Object.entries(partialData.stats)
                .filter(([tipo]) => tipo.startsWith(prefix))
                .map(([tipo, values]) => ({
                    tipoOriginal: tipo,
                    tipoNormalized: normalizePaymentMethod(stripPrefix(tipo)),
                    count: values.count,
                    total: values.total,
                    details: partialData.details?.[tipo] || [],
                }))
            : [];

    const pedidoEntries = useMemo(() => processEntries('Pedido - '), [partialData]);
    const recargaEntries = useMemo(() => processEntries('Recarga - '), [partialData]);
    const correcaoEntries = useMemo(() => processEntries('Correção'), [partialData]);
    const correctionsTotal = useMemo(
        () => correcaoEntries.reduce((sum, entry) => sum + (Number(entry.total) || 0), 0),
        [correcaoEntries]
    );
    // Pagamentos online (PIX/cartão via foodz-payments, recargas via Asaas): dinheiro que foi
    // direto para a carteira digital, nunca passou pela gaveta física. Apenas informativo —
    // nunca soma ao Valor Final do caixa físico nem precisa de conferência manual.
    const pedidoOnlineEntries = useMemo(() => processEntries('Pedido Online - '), [partialData]);
    const recargaOnlineEntries = useMemo(() => processEntries('Recarga Online - '), [partialData]);
    // Vendas com Crédito Foodz: dinheiro que já entrou no caixa quando o crédito foi recarregado —
    // informativo apenas, não soma de novo ao Valor Final.
    const pedidoCreditoFoodzEntries = useMemo(() => processEntries('Pedido Crédito Foodz - '), [partialData]);

    const handleEditClick = (field) => {
        setFieldToEdit(field);
        setShowModal(true);
    };

    const handleSaveTransaction = async (formData) => {
        if (isSubmittingRef.current) return;

        const clientCode = getClientCode();
        if (!clientCode) {
            toast.error("Sessão expirada.");
            return;
        }

        isSubmittingRef.current = true;
        setLoading(true);

        const isInput = fieldToEdit === 'inputValue';
        const amount = parseFloat(formData.value);

        try {
            const newTransaction = {
                paymentMethod: formData.paymentMethod,
                value: amount,
                received_value: amount,
                client_code: clientCode,
                transactionDate: ajustedDate(),
                transaction_type: isInput ? "Registry Incoming" : "Registry Outgoing",
                customer_id: 'management',
                transactionSource: 'MANUAL'
            };

            const currentInputs = Number(dailyCashFlow.inputs_partial_value) || 0;
            const currentOutputs = Number(dailyCashFlow.outputs_partial_value) || 0;
            const startValue = Number(dailyCashFlow.start_value) || 0;

            const newInputs = isInput ? currentInputs + amount : currentInputs;
            const newOutputs = !isInput ? currentOutputs + amount : currentOutputs;
            const newFinalValue = startValue + newInputs - newOutputs;

            const updatedDailyCashFlow = {
                ...dailyCashFlow,
                inputs_partial_value: newInputs,
                outputs_partial_value: newOutputs,
                final_value: newFinalValue,
            };

            await FinancialService.addRegistryTransactionAndUpdate(newTransaction, updatedDailyCashFlow);

            setDailyCashFlow(updatedDailyCashFlow);
            isInput ? setInputValue(newInputs) : setOutputValue(newOutputs);

            if (onTransactionUpdate) onTransactionUpdate(newTransaction);
            setShowModal(false);
            toast.success("Transação lançada com sucesso!");
        } catch (error) {
            console.error(error);
            toast.error("Erro ao salvar transação.");
        } finally {
            isSubmittingRef.current = false;
            setLoading(false);
        }
    };

    const handleSubmitOpen = async () => {
        if (isSubmittingRef.current) return;

        if (!dailyCashFlow.client_code) {
            toast.error("Sessão inválida.");
            return;
        }

        isSubmittingRef.current = true;
        setLoading(true);

        try {
            const openedData = await FinancialService.openDailyCashFlow(dailyCashFlow);

            const safeOpeningTime = openedData?.opening_time || ajustedDate();
            const safeStartValue = openedData?.start_value !== undefined
                ? Number(openedData.start_value)
                : Number(dailyCashFlow.start_value || 0);

            const newCashFlowState = {
                ...dailyCashFlow,
                ...(openedData || {}),
                registry_status: 'opened',
                opening_time: safeOpeningTime,
                start_value: safeStartValue
            };

            setDailyCashFlow(newCashFlowState);
            setIsOpened(true);
            setOpen(false);
            setInputValue(0);
            setOutputValue(0);

            toast.success("Caixa aberto com sucesso!");

        } catch (error) {
            console.error("Erro ao abrir caixa:", error);

            if (error.response) {
                const status = error.response.status;
                const serverMessage = error.response.data;

                if (status === 409) {
                    toast.warning(serverMessage || "Já existe um caixa aberto!");
                    return;
                }

                if (status === 400) {
                    toast.error("Dados inválidos para abertura.");
                    return;
                }
            }

            toast.error("Erro interno ao abrir caixa. Tente novamente.");
        } finally {
            isSubmittingRef.current = false;
            setLoading(false);
        }
    };

    const openDailyCashFlowUI = () => {
        setOpen(!open);
        setDailyCashFlow(prev => ({
            ...prev,
            registry_status: 'opened',
            opening_time: ajustedDate(),
            opened_by: getUserName(),
        }));
    };

    const handleCheckButtonClick = (tipoOriginal, count, total) => {
        const isCorrection = tipoOriginal.startsWith("Correção");
        const parsedTotal = Number(total) || 0;
        const wasChecked = disabledButtons[tipoOriginal];

        setDisabledButtons(prev => ({ ...prev, [tipoOriginal]: !wasChecked }));

        let valueChange = 0;
        if (!wasChecked) {
            valueChange = isCorrection ? -parsedTotal : parsedTotal;
        } else {
            valueChange = isCorrection ? parsedTotal : -parsedTotal;
        }

        const newFinalValue = (dailyCashFlow.final_value || 0) + valueChange;
        const line = `Tipo de Pagamento: ${tipoOriginal}, Contagem: ${count}, Valor Parcial: R$${parsedTotal.toFixed(2)}, Status: Conferido\n`;

        let newReport = dailyCashFlow.closing_report || '';

        if (!wasChecked) {
            newReport += line;
        } else {
            newReport = newReport.replace(line, '');
        }

        setDailyCashFlow(prev => ({
            ...prev,
            final_value: newFinalValue,
            closing_report: newReport,
            closing_status: newReport,
        }));
    };

    const finishCashBalanceRegistry = async () => {
        if (isSubmittingRef.current) return;

        if (!dailyCashFlow.client_code) {
            toast.error("Sessão inválida.");
            return;
        }

        isSubmittingRef.current = true;
        setLoading(true);

        const reportSummary =
            `Entradas - Valor: R$${dailyCashFlow.inputs_partial_value.toFixed(2)}\n` +
            `Saídas - Valor: R$${dailyCashFlow.outputs_partial_value.toFixed(2)}\n` +
            `Recargas de Crédito - Valor: R$${creditCharges.toFixed(2)}\n` +
            `Correções de Crédito - Valor: R$${correctionsTotal.toFixed(2)}\n` +
            `Pedidos Cancelados - ${canceledSummary.count}  Valor Total: R$${canceledSummary.total.toFixed(2)}\n`;

        const closingReportData = {
            entradas: dailyCashFlow.inputs_partial_value || 0,
            saidas: dailyCashFlow.outputs_partial_value || 0,
            itens: [...pedidoEntries, ...recargaEntries, ...correcaoEntries].map(entry => ({
                tipo: entry.tipoOriginal.startsWith('Pedido') ? 'pedido'
                    : entry.tipoOriginal.startsWith('Correção') ? 'correcao'
                        : 'recarga',
                tipoPagamento: entry.tipoOriginal,
                contagem: entry.count,
                valor: entry.total,
            })),
            // Informativo apenas: dinheiro que foi direto para a carteira digital (foodz-payments),
            // nunca passou pela gaveta física, não soma ao final_value.
            itensOnline: [...pedidoOnlineEntries, ...recargaOnlineEntries].map(entry => ({
                tipo: entry.tipoOriginal.startsWith('Pedido') ? 'pedido' : 'recarga',
                tipoPagamento: entry.tipoOriginal,
                contagem: entry.count,
                valor: entry.total,
            })),
            // Informativo apenas: crédito que já entrou no caixa quando foi recarregado, não soma
            // de novo ao final_value na hora da venda.
            itensCreditoFoodz: pedidoCreditoFoodzEntries.map(entry => ({
                tipo: 'pedido',
                tipoPagamento: entry.tipoOriginal,
                contagem: entry.count,
                valor: entry.total,
            })),
            pedidosCancelados: { count: canceledSummary.count, total: canceledSummary.total },
        };

        const updatedDailyCashFlow = {
            ...dailyCashFlow,
            registry_status: 'closed',
            total_credit_charges: creditCharges,
            closing_report: (dailyCashFlow.closing_report || '') + reportSummary,
            closing_report_data: JSON.stringify(closingReportData),
            closing_time: ajustedDate(),
            closed_by: getUserName(),
        };

        try {
            await FinancialService.closeDailyCashFlow(updatedDailyCashFlow);

            setIsOpened(false);
            setIsClosed(false);
            setOpen(false);
            setDailyCashFlow(prev => resetCashFlow(prev.client_code));
            setPartialData(null);
            setDisabledButtons({});
            setInputValue(0);
            setOutputValue(0);

            toast.success("Caixa fechado com sucesso!");
        } catch (error) {
            console.error(error);
            if (error.response?.status === 400) {
                toast.error(error.response.data.message);
                setIsClosed(false);
            } else {
                toast.error("Erro ao fechar caixa.");
            }
        } finally {
            isSubmittingRef.current = false;
            setLoading(false);
        }
    };

    const isFinishButtonEnabled = useMemo(() => {
        const physicalStatsKeys = partialData?.stats
            ? Object.keys(partialData.stats).filter(k => !k.startsWith('Pedido Online') && !k.startsWith('Recarga Online') && !k.startsWith('Pedido Crédito Foodz'))
            : [];

        const hasNoData =
            physicalStatsKeys.length === 0 &&
            pedidoEntries.length === 0 &&
            recargaEntries.length === 0 &&
            correcaoEntries.length === 0 &&
            Number(inputValue) === 0 &&
            Number(outputValue) === 0;

        if (hasNoData) return true;

        if (physicalStatsKeys.length === 0) return false;

        return physicalStatsKeys.every(k => Boolean(disabledButtons[k]));
    }, [
        partialData,
        disabledButtons,
        inputValue,
        outputValue,
        pedidoEntries,
        recargaEntries,
        correcaoEntries
    ]);

    return (
        <div className="h-full flex flex-col font-poppins relative">
            <div className="flex items-center justify-between mb-2 sticky top-0 bg-white z-10 py-2 border-b border-slate-100">
                <h2 className="font-bold text-lg text-gray-800">
                    {isClosed ? "Conferência Final" : isOpened ? "Caixa Aberto" : "Abertura de Caixa"}
                </h2>
                <ModalInfo
                    icon={<RiErrorWarningLine />}
                    content="Atenção! Pedidos não finalizados não aparecem no balanço parcial."
                />
            </div>

            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
                {isClosed ? (
                    <ClosedCashView
                        dailyCashFlow={dailyCashFlow}
                        inputValue={inputValue}
                        outputValue={outputValue}
                        pedidoEntries={pedidoEntries}
                        recargaEntries={recargaEntries}
                        correcaoEntries={correcaoEntries}
                        pedidoOnlineEntries={pedidoOnlineEntries}
                        recargaOnlineEntries={recargaOnlineEntries}
                        pedidoCreditoFoodzEntries={pedidoCreditoFoodzEntries}
                        disabledButtons={disabledButtons}
                        loading={loading}
                        handleButtonClick={handleCheckButtonClick}
                        handleBackClick={() => setIsClosed(false)}
                        finishCashBalanceRegistry={finishCashBalanceRegistry}
                        handleEditClick={handleEditClick}
                        isFinishButtonEnabled={isFinishButtonEnabled}
                    />
                ) : isOpened ? (
                    <OpenedCashView
                        dailyCashFlow={dailyCashFlow}
                        inputValue={inputValue}
                        outputValue={outputValue}
                        pedidoEntries={pedidoEntries}
                        recargaEntries={recargaEntries}
                        correcaoEntries={correcaoEntries}
                        pedidoOnlineEntries={pedidoOnlineEntries}
                        recargaOnlineEntries={recargaOnlineEntries}
                        pedidoCreditoFoodzEntries={pedidoCreditoFoodzEntries}
                        loading={loading}
                        handleEditClick={handleEditClick}
                        setIsClosed={setIsClosed}
                    />
                ) : (
                    <OpenCashInitialView
                        dailyCashFlow={dailyCashFlow}
                        setDailyCashFlow={setDailyCashFlow}
                        handleInputChange={(e) =>
                            setDailyCashFlow(prev => ({
                                ...prev,
                                [e.target.name]: e.target.value
                            }))
                        }
                        open={open}
                        openDailyCashFlow={openDailyCashFlowUI}
                        handleSubmit={handleSubmitOpen}
                        loading={loading}
                        today={todayLabel}
                    />
                )}
            </div>

            <RegistryTransactionModal
                isOpen={showModal}
                type={fieldToEdit}
                onClose={() => setShowModal(false)}
                onConfirm={handleSaveTransaction}
                loading={loading}
            />
        </div>
    );
};

export default Registry;